/**
 * Screenlux Zip-Screen 2 Guarantee Front-end Controller
 */

// Global state holding current active screen info for the modal
let activeScreenData = null;
let currentSelectedFileBase64 = null;

// Review step state (shown after a successful activation when the app asks for it)
let guaranteeActivated = false;   // an activation succeeded on this page view → closing reloads
let guaranteeReloadScheduled = false;
let reviewContext = null;         // { orderId, screenIndex } of the screen just activated
let reviewRating = 0;
let reviewRequest = null;         // in-flight review POST, awaited before a reload

const GUARANTEE_DEFAULT_WINDOW_DAYS = 60;
const GUARANTEE_DAY_MS = 86400000;

// Determine locale from HTML lang or URL
const docLang = document.documentElement.lang.toLowerCase();
const currentLocale = (docLang.startsWith('no') || docLang.startsWith('nb') || docLang.startsWith('nn')) ? 'no' : 'de';

const labels = {
  no: {
    status_unfulfilled: 'Venter på levering',
    status_pending: 'Venter på aktivering',
    status_active: 'Aktiv',
    status_expired_initial: 'Utløpt (Ikke registrert innen {days} dager)',
    status_expired_deadline: 'Utløpt (Ikke aktivert innen {date})',
    status_expired_lifetime: 'Utløpt (Garantitid over)',
    covered: 'Dekket (til {date})',
    expired: 'Utløpt ({date})',
    not_applicable: 'Ikke aktuelt',
    days_left: '({days} dager igjen)',
    one_day_left: '(1 dag igjen)',
    last_day: '(kun i dag)',
    expired_label: 'Utløpt',
    error_no_file: 'Vennligst velg et bilde.',
    error_checkboxes: 'Du må krysse av for alle punktene.',
    error_generic: 'Noe gikk galt. Vennligst prøv igjen.',
    btn_activate: 'Aktiver garanti',
    activated_title: 'Garanti aktivert ✓'
  },
  de: {
    status_unfulfilled: 'Wartet auf Lieferung',
    status_pending: 'Aktivierung ausstehend',
    status_active: 'Aktiv',
    status_expired_initial: 'Abgelaufen (Nicht innerhalb von {days} Tagen aktiviert)',
    status_expired_deadline: 'Abgelaufen (Nicht bis {date} aktiviert)',
    status_expired_lifetime: 'Abgelaufen (Garantiefrist beendet)',
    covered: 'Abgedeckt (bis {date})',
    expired: 'Abgelaufen ({date})',
    not_applicable: 'Nicht zutreffend',
    days_left: '({days} Tage übrig)',
    one_day_left: '(1 Tag übrig)',
    last_day: '(nur noch heute)',
    expired_label: 'Abgelaufen',
    error_no_file: 'Bitte wählen Sie ein Foto aus.',
    error_checkboxes: 'Bitte bestätigen Sie alle Kontrollkästchen.',
    error_generic: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
    btn_activate: 'Garantie aktivieren',
    activated_title: 'Garantie aktiviert ✓'
  }
};

function formatGuaranteeDate(date) {
  return date.toLocaleDateString(currentLocale === 'no' ? 'no-NO' : 'de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Global activation window in days (shop metafield custom.guarantee_window_days,
 * exposed by the section as window.screenluxGuaranteeWindowDays). Defaults to 60.
 */
function getGuaranteeWindowDays() {
  const n = parseInt(window.screenluxGuaranteeWindowDays, 10);
  return Number.isFinite(n) && n >= 0 ? n : GUARANTEE_DEFAULT_WINDOW_DAYS;
}

/**
 * Per-screen override from the order metafield custom.guarantee_deadlines:
 * "YYYY-MM-DD" = the last day (inclusive, local time) the customer may activate.
 * Returns the local start of that day, or null when absent/invalid.
 */
function parseActivationDeadline(str) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(str || '').trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(year, month, day);
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
  return d;
}

/**
 * Computes the guarantee status and components coverage based on dates.
 * activationDeadlineStr (optional) overrides fulfilment + window days.
 */
function calculateGuaranteeStatus(screen, orderDateStr, fulfillmentDateStr, activationDeadlineStr) {
  const today = new Date();
  const orderDate = new Date(orderDateStr);
  const fulfillmentDate = fulfillmentDateStr ? new Date(fulfillmentDateStr) : null;
  const isFulfilled = !!fulfillmentDate;

  // 1. Check if unfulfilled
  if (!isFulfilled) {
    return {
      status: 'AWAITING_FULFILLMENT',
      label: labels[currentLocale].status_unfulfilled,
      badgeClass: 'sl-badge-unfulfilled',
      canAction: false
    };
  }

  const activatedAt = screen?.first_activated_at ? new Date(screen.first_activated_at) : null;
  const nextRenewal = screen?.next_renewal_due ? new Date(screen.next_renewal_due) : null;

  // 2. Check if unregistered
  if (!activatedAt) {
    // Last activation day (inclusive, local calendar day): the per-screen
    // override when the app set one, else fulfilment day + window days.
    const overrideDay = parseActivationDeadline(activationDeadlineStr);
    const windowDays = getGuaranteeWindowDays();
    let deadlineDay = overrideDay;
    if (!deadlineDay) {
      deadlineDay = startOfLocalDay(fulfillmentDate);
      deadlineDay.setDate(deadlineDay.getDate() + windowDays);
    }

    // Math.round absorbs the 23/25 h days around DST switches
    const daysRemaining = Math.round((deadlineDay - startOfLocalDay(today)) / GUARANTEE_DAY_MS);

    if (daysRemaining < 0) {
      return {
        status: 'EXPIRED_UNREGISTERED',
        label: overrideDay
          ? labels[currentLocale].status_expired_deadline.replace('{date}', formatGuaranteeDate(overrideDay))
          : labels[currentLocale].status_expired_initial.replace('{days}', windowDays),
        badgeClass: 'sl-badge-expired',
        canAction: false
      };
    }

    let daysLeftText;
    if (daysRemaining === 0) {
      daysLeftText = labels[currentLocale].last_day;
    } else if (daysRemaining === 1) {
      daysLeftText = labels[currentLocale].one_day_left;
    } else {
      daysLeftText = labels[currentLocale].days_left.replace('{days}', daysRemaining);
    }

    return {
      status: 'PENDING_ACTIVATION',
      label: `${labels[currentLocale].status_pending} ${daysLeftText}`,
      badgeClass: 'sl-badge-pending',
      canAction: true,
      actionLabel: labels[currentLocale].btn_activate,
      activationDeadline: deadlineDay
    };
  }

  // 3. Calculate component coverages from Order Date
  const msSinceOrder = today - orderDate;
  const elapsedYears = msSinceOrder / (365.25 * 86400000);

  const componentLimits = {
    structure: 10,
    rest: 5
  };

  const coverage = {};
  let anyActive = false;

  for (const [key, maxYears] of Object.entries(componentLimits)) {
    const endCoverageDate = new Date(orderDate);
    endCoverageDate.setFullYear(orderDate.getFullYear() + maxYears);
    const isActive = today < endCoverageDate;
    
    if (isActive) anyActive = true;

    const formattedDate = formatGuaranteeDate(endCoverageDate);

    coverage[key] = {
      active: isActive,
      label: isActive 
        ? labels[currentLocale].covered.replace('{date}', formattedDate)
        : labels[currentLocale].expired.replace('{date}', formattedDate)
    };
  }

  // If all component terms have expired, the guarantee is fully expired
  if (!anyActive) {
    return {
      status: 'EXPIRED_LIFETIME',
      label: labels[currentLocale].status_expired_lifetime,
      badgeClass: 'sl-badge-expired',
      canAction: false,
      coverage
    };
  }

  return {
    status: 'ACTIVE',
    label: labels[currentLocale].status_active,
    badgeClass: 'sl-badge-active',
    canAction: true,
    actionLabel: labels[currentLocale].status_active,
    coverage
  };
}

/**
 * Initializes the guarantee widgets on page load
 */
document.addEventListener('DOMContentLoaded', () => {
  // Initialize click outside to close for the modal overlay if present
  const modal = document.getElementById('screenlux-guarantee-modal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeGuaranteeModal();
      }
    });
    initGuaranteeReviewStars(modal);
  }

  // Escape closes the modal (and reloads after a successful activation)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isGuaranteeModalOpen()) {
      closeGuaranteeModal();
    }
  });

  // Intercept click on any link with href="#guarantee-terms"
  document.addEventListener('click', (e) => {
    const guaranteeLink = e.target.closest('a[href="#guarantee-terms"]');
    if (guaranteeLink) {
      e.preventDefault();
      if (typeof window.openGeneralGuaranteeTerms === 'function') {
        window.openGeneralGuaranteeTerms();
      }
    }
  });

  const widgets = document.querySelectorAll('.sl-guarantee-widget');
  if (widgets.length === 0) return;

  widgets.forEach(widget => {
    const screenIndex = parseInt(widget.dataset.screenIndex, 10);
    const orderId = widget.dataset.orderId;
    const orderName = widget.dataset.orderName;
    const orderDate = widget.dataset.orderDate;
    const fulfillmentDate = widget.dataset.fulfillmentDate || null;
    const screenName = widget.dataset.screenName;
    const activationDeadline = widget.dataset.activationDeadline || null;

    // Retrieve full list of customer guarantees from window global variable (passed from Liquid)
    const guaranteesList = window.screenluxCustomerGuarantees || [];
    const screenRecord = guaranteesList.find(g => g.order_id === orderId && g.screen_index === screenIndex);

    // Calculate status
    const result = calculateGuaranteeStatus(screenRecord, orderDate, fulfillmentDate, activationDeadline);

    // Update UI elements
    const statusTextEl = widget.querySelector('.sl-guarantee-status-text');
    const badgeEl = widget.querySelector('.sl-guarantee-badge');
    const actionBtn = widget.querySelector('.sl-guarantee-action-btn');

    if (badgeEl) {
      badgeEl.className = `sl-guarantee-badge ${result.badgeClass}`;
      badgeEl.textContent = result.label;
    }

    if (actionBtn) {
      if (result.status === 'PENDING_ACTIVATION') {
        actionBtn.style.display = 'inline-block';
        actionBtn.textContent = labels[currentLocale].btn_activate;
        actionBtn.className = 'sl-guarantee-action-btn sl-btn-activate';
      } else if (result.status === 'AWAITING_FULFILLMENT') {
        actionBtn.style.display = 'none';
      } else {
        // Active or Expired
        actionBtn.style.display = 'inline-block';
        actionBtn.textContent = currentLocale === 'no' ? 'Vis detaljer' : 'Details anzeigen';
        actionBtn.className = 'sl-guarantee-action-btn sl-btn-details';
      }

      // Hook click event
      actionBtn.addEventListener('click', (e) => {
        e.preventDefault();
        openGuaranteeModal({
          screenRecord,
          orderId,
          orderName,
          orderDate,
          fulfillmentDate,
          screenIndex,
          screenName,
          computed: result
        });
      });
    }
  });
});

/**
 * Open Modal with the specific context
 */
function openGuaranteeModal(data) {
  activeScreenData = data;
  currentSelectedFileBase64 = null;
  
  const modal = document.getElementById('screenlux-guarantee-modal');
  const titleEl = document.getElementById('sl-modal-title');
  const subtitleEl = document.getElementById('sl-modal-subtitle');
  
  const formActivate = document.getElementById('sl-form-activate');
  const panelDetails = document.getElementById('sl-panel-details');
  const panelReview = document.getElementById('sl-panel-review');
  if (panelReview) panelReview.style.display = 'none';

  // Reset forms
  formActivate.reset();
  document.getElementById('sl-preview-activate').style.display = 'none';
  document.getElementById('sl-error-activate').style.display = 'none';

  // Set headings
  titleEl.textContent = data.screenName;
  subtitleEl.textContent = `${activeScreenData.orderName} (Index ${activeScreenData.screenIndex + 1})`;

  // Display correct panel
  if (data.computed.status === 'PENDING_ACTIVATION') {
    titleEl.textContent = `${currentLocale === 'no' ? 'Aktiver' : 'Aktivieren'}: ${data.screenName}`;
    formActivate.style.display = 'block';
    panelDetails.style.display = 'none';
  } else {
    // Details panel for active or expired states
    formActivate.style.display = 'none';
    panelDetails.style.display = 'block';

    // Set meta fields
    document.getElementById('sl-meta-order-date').textContent = new Date(data.orderDate).toLocaleDateString(currentLocale === 'no' ? 'no-NO' : 'de-DE');
    document.getElementById('sl-meta-fulfillment-date').textContent = data.fulfillmentDate 
      ? new Date(data.fulfillmentDate).toLocaleDateString(currentLocale === 'no' ? 'no-NO' : 'de-DE')
      : '-';

    const badge = document.getElementById('sl-meta-status-badge');
    badge.className = `sl-guarantee-badge ${data.computed.badgeClass}`;
    badge.textContent = data.computed.label;

    // Fill table statuses
    const rows = document.querySelectorAll('.sl-coverage-table tbody tr');
    rows.forEach(row => {
      const componentKey = row.dataset.component;
      const statusCell = row.querySelector('.sl-td-status');
      
      if (data.computed.coverage && data.computed.coverage[componentKey]) {
        const item = data.computed.coverage[componentKey];
        statusCell.className = `sl-td-status ${item.active ? 'sl-status-covered' : 'sl-status-expired'}`;
        statusCell.textContent = item.label;
      } else {
        // If unregistered or expired initial, everything is fully expired / ineligible
        statusCell.className = 'sl-td-status sl-status-expired';
        statusCell.textContent = labels[currentLocale].expired_label;
      }
    });

    // Render photo history if available
    const latestPhotoEl = document.getElementById('sl-latest-photo');
    const photoContainer = document.getElementById('sl-latest-photo-container');
    if (data.screenRecord?.photo_url) {
      latestPhotoEl.src = data.screenRecord.photo_url;
      photoContainer.style.display = 'block';
    } else {
      photoContainer.style.display = 'none';
    }
  }

  // Show modal overlay
  modal.style.display = 'flex';
  modal.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
}

/**
 * Close Modal
 */
function closeGuaranteeModal() {
  const modal = document.getElementById('screenlux-guarantee-modal');
  if (!modal) return;

  // After a successful activation the widget must show "Active": reload, but
  // let an in-flight review POST land first (capped so a hung request can't block).
  if (guaranteeActivated && !guaranteeReloadScheduled) {
    guaranteeReloadScheduled = true;
    const reload = () => window.location.reload();
    if (reviewRequest) {
      reviewRequest.then(reload, reload);
      setTimeout(reload, 4000);
    } else {
      reload();
    }
  }

  modal.style.display = 'none';
  modal.setAttribute('aria-hidden', 'true');
  modal.removeAttribute('data-view');
  document.body.style.overflow = '';
  activeScreenData = null;
  currentSelectedFileBase64 = null;
}

/**
 * Expose a global helper to open the general terms view of the guarantee modal (e.g. from footer)
 */
window.openGeneralGuaranteeTerms = function() {
  const modal = document.getElementById('screenlux-guarantee-modal');
  if (!modal) return;

  const titleEl = document.getElementById('sl-modal-title');
  const subtitleEl = document.getElementById('sl-modal-subtitle');
  const formActivate = document.getElementById('sl-form-activate');
  const panelDetails = document.getElementById('sl-panel-details');

  const panelReview = document.getElementById('sl-panel-review');
  if (panelReview) panelReview.style.display = 'none';

  // Set general terms title
  titleEl.textContent = currentLocale === 'no' ? 'Garantibetingelser' : 'Garantiebedingungen';
  subtitleEl.textContent = ''; // Clear subtitle
  formActivate.style.display = 'none';
  panelDetails.style.display = 'block';

  // Set view mode to general
  modal.setAttribute('data-view', 'general');

  // Show modal
  modal.style.display = 'flex';
  modal.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
};

window.closeGuaranteeModal = closeGuaranteeModal;

/**
 * Programmatically clicks the hidden file input
 */
function triggerFileInput(formType) {
  document.getElementById(`sl-file-${formType}`).click();
}

/**
 * Reads the selected image file and encodes it to Base64
 */
function handleFileSelect(event, formType) {
  event.stopPropagation();
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    currentSelectedFileBase64 = e.target.result;
    
    // Render a small image preview
    const previewContainer = document.getElementById(`sl-preview-${formType}`);
    previewContainer.innerHTML = `<img src="${currentSelectedFileBase64}" alt="Upload preview">`;
    previewContainer.style.display = 'block';
    
    // Hide error if shown
    document.getElementById(`sl-error-${formType}`).style.display = 'none';
  };
  reader.readAsDataURL(file);
}

/**
 * Handles Form Submission for activation & renewal
 */
async function submitGuaranteeForm(event, formType) {
  event.preventDefault();

  const form = event.target;
  const errorEl = document.getElementById(`sl-error-${formType}`);
  const spinner = form.querySelector('.sl-spinner');
  const btnText = form.querySelector('.sl-btn-text');

  // Verify file upload
  if (!currentSelectedFileBase64) {
    errorEl.textContent = labels[currentLocale].error_no_file;
    errorEl.style.display = 'block';
    return;
  }

  // Verify checkboxes
  const checkboxes = form.querySelectorAll('.sl-checkbox-input');
  let allChecked = true;
  checkboxes.forEach(cb => {
    if (!cb.checked) allChecked = false;
  });

  if (!allChecked) {
    errorEl.textContent = labels[currentLocale].error_checkboxes;
    errorEl.style.display = 'block';
    return;
  }

  // Show loading
  if (spinner) spinner.style.display = 'inline-block';
  if (btnText) btnText.style.opacity = '0.5';
  const submitBtn = form.querySelector('button[type="submit"]');
  if (submitBtn) submitBtn.disabled = true;

  // Captured before the await: the modal may be closed (activeScreenData reset) meanwhile
  const activatedScreen = {
    orderId: activeScreenData.orderId,
    screenIndex: activeScreenData.screenIndex
  };

  try {
    const payload = {
      orderId: activeScreenData.orderId,
      orderName: activeScreenData.orderName,
      orderDate: activeScreenData.orderDate,
      screenIndex: activeScreenData.screenIndex,
      screenName: activeScreenData.screenName,
      fulfillmentDate: activeScreenData.fulfillmentDate,
      image: currentSelectedFileBase64,
      type: formType
    };

    // Grab customer ID from URL or window variable
    const customerId = window.screenluxCustomerId;

    const response = await fetch(`/apps/measurements?action=update-guarantee&customer_id=${customerId}&logged_in_customer_id=${customerId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok || result.error) {
      throw new Error(result.error || labels[currentLocale].error_generic);
    }

    guaranteeActivated = true;

    // Same screen still open and the app wants a review → review step.
    // Otherwise reload so the Liquid/metafield state shows the activation.
    const sameScreenOpen = isGuaranteeModalOpen()
      && activeScreenData
      && activeScreenData.orderId === activatedScreen.orderId
      && activeScreenData.screenIndex === activatedScreen.screenIndex;

    if (result.askReview === true && sameScreenOpen) {
      showGuaranteeReviewStep(activatedScreen);
    } else {
      guaranteeReloadScheduled = true;
      window.location.reload();
    }

  } catch (err) {
    console.error('Error submitting guarantee form:', err);
    errorEl.textContent = err.message || labels[currentLocale].error_generic;
    errorEl.style.display = 'block';

    // Hide loading
    if (spinner) spinner.style.display = 'none';
    if (btnText) btnText.style.opacity = '1';
    if (submitBtn) submitBtn.disabled = false;
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Review step (after a successful activation, when the app returns askReview)
 * Mirrors the content-collect review: stars → optional text → optional
 * release → thank-you, plus a Trustpilot prompt after 5 stars.
 * ────────────────────────────────────────────────────────────────────────── */

function isGuaranteeModalOpen() {
  const modal = document.getElementById('screenlux-guarantee-modal');
  return !!modal && modal.style.display !== 'none' && modal.getAttribute('aria-hidden') !== 'true';
}

function getReviewStars() {
  const panel = document.getElementById('sl-panel-review');
  return panel ? Array.from(panel.querySelectorAll('[data-star]')) : [];
}

/**
 * Paints the stars for `value` (0 = none) and keeps the radiogroup's roving
 * tabindex on the checked star (or the first one when nothing is chosen).
 */
function setGuaranteeReviewRating(value, focus) {
  reviewRating = value;
  const stars = getReviewStars();
  stars.forEach((star) => {
    const n = Number(star.dataset.star);
    star.classList.toggle('is-on', n <= value);
    star.setAttribute('aria-checked', String(n === value));
    star.tabIndex = n === (value || 1) ? 0 : -1;
  });
  const submitBtn = document.getElementById('sl-review-submit');
  if (submitBtn) submitBtn.disabled = !value;
  if (focus && value && stars[value - 1]) stars[value - 1].focus();
}

function initGuaranteeReviewStars(modal) {
  const group = modal.querySelector('#sl-panel-review [role="radiogroup"]');
  if (!group) return;

  group.addEventListener('click', (e) => {
    const star = e.target.closest('[data-star]');
    if (!star) return;
    setGuaranteeReviewRating(Number(star.dataset.star), true);
  });

  // WAI-ARIA radiogroup keys: arrows move + select (wrapping), Home/End jump
  group.addEventListener('keydown', (e) => {
    const star = e.target.closest('[data-star]');
    if (!star) return;
    const current = Number(star.dataset.star);
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = current >= 5 ? 1 : current + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = current <= 1 ? 5 : current - 1;
    else if (e.key === 'Home') next = 1;
    else if (e.key === 'End') next = 5;
    if (next === null) return;
    e.preventDefault();
    setGuaranteeReviewRating(next, true);
  });
}

/**
 * Swaps the modal from the activation form to "activated ✓" + review form.
 */
function showGuaranteeReviewStep(screen) {
  const panelReview = document.getElementById('sl-panel-review');
  if (!panelReview) {
    // Markup missing (e.g. an older cached snippet): fall back to the old flow
    guaranteeReloadScheduled = true;
    window.location.reload();
    return;
  }

  reviewContext = { orderId: screen.orderId, screenIndex: screen.screenIndex };
  reviewRequest = null;

  document.getElementById('sl-form-activate').style.display = 'none';
  document.getElementById('sl-panel-details').style.display = 'none';

  // Reset the review form
  setGuaranteeReviewRating(0, false);
  const textEl = document.getElementById('sl-review-text');
  if (textEl) textEl.value = '';
  const consentEl = document.getElementById('sl-review-consent');
  if (consentEl) consentEl.checked = false;
  const submitBtn = document.getElementById('sl-review-submit');
  if (submitBtn) {
    const spinner = submitBtn.querySelector('.sl-spinner');
    const btnText = submitBtn.querySelector('.sl-btn-text');
    if (spinner) spinner.style.display = 'none';
    if (btnText) btnText.style.opacity = '1';
  }
  document.getElementById('sl-review-form').style.display = 'flex';
  document.getElementById('sl-review-done').style.display = 'none';
  document.getElementById('sl-review-trustpilot').style.display = 'none';

  const titleEl = document.getElementById('sl-modal-title');
  titleEl.textContent = labels[currentLocale].activated_title;

  panelReview.style.display = 'block';

  // Move focus into the new content for keyboard / screen-reader users
  titleEl.setAttribute('tabindex', '-1');
  titleEl.focus();
  const container = panelReview.closest('.sl-modal-container');
  if (container) container.scrollTop = 0;
}

/**
 * Sends the review. Never shows an error: the activation already succeeded,
 * so a failed review is logged and the customer still gets the thank-you.
 */
async function submitGuaranteeReview() {
  if (!reviewContext || !reviewRating || reviewRequest) return;

  const rating = reviewRating;
  const submitBtn = document.getElementById('sl-review-submit');
  const spinner = submitBtn ? submitBtn.querySelector('.sl-spinner') : null;
  const btnText = submitBtn ? submitBtn.querySelector('.sl-btn-text') : null;
  if (submitBtn) submitBtn.disabled = true;
  if (spinner) spinner.style.display = 'inline-block';
  if (btnText) btnText.style.opacity = '0.5';
  const skipBtn = document.getElementById('sl-review-skip');
  if (skipBtn) skipBtn.disabled = true;

  const textEl = document.getElementById('sl-review-text');
  const consentEl = document.getElementById('sl-review-consent');
  const consentTextEl = document.getElementById('sl-review-consent-text');

  const payload = {
    orderId: reviewContext.orderId,
    screenIndex: reviewContext.screenIndex,
    rating,
    text: textEl ? textEl.value.trim() : '',
    consent: !!(consentEl && consentEl.checked),
    // The exact release wording the customer saw next to the checkbox
    consentText: consentTextEl ? consentTextEl.textContent.replace(/\s+/g, ' ').trim() : ''
  };

  const customerId = window.screenluxCustomerId;

  reviewRequest = (async () => {
    try {
      const response = await fetch(`/apps/measurements?action=guarantee-review&customer_id=${customerId}&logged_in_customer_id=${customerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      let result = null;
      try { result = await response.json(); } catch (_) { /* non-JSON body */ }
      if (!response.ok || !result || result.error) {
        throw new Error((result && result.error) || `HTTP ${response.status}`);
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'guarantee_review_submitted', { rating });
      }
    } catch (err) {
      console.error('Guarantee review submit failed:', err);
    }
  })();

  await reviewRequest;
  showGuaranteeReviewDone(rating);
}

function showGuaranteeReviewDone(rating) {
  const formEl = document.getElementById('sl-review-form');
  const doneEl = document.getElementById('sl-review-done');
  if (formEl) formEl.style.display = 'none';
  if (doneEl) doneEl.style.display = 'block';

  // Five stars is someone happy enough to say so in public too
  const tp = document.getElementById('sl-review-trustpilot');
  if (tp) tp.style.display = rating === 5 ? 'block' : 'none';

  const focusTarget = document.getElementById('sl-review-done-text');
  if (focusTarget) {
    focusTarget.setAttribute('tabindex', '-1');
    focusTarget.focus();
  }
}

function trackGuaranteeTrustpilotClick() {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'guarantee_trustpilot_click');
  }
}

window.submitGuaranteeReview = submitGuaranteeReview;
window.trackGuaranteeTrustpilotClick = trackGuaranteeTrustpilotClick;
